import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import React from 'react';

import { SectionHeading, FieldLabel, CollapsibleRow } from './components';
import { type ResourceDataType, type ResourceType } from './types';

import TextEditor from '@/TextEditor';
import { GREY2, GREY } from '@/theme/palette';

const NOTES_PLACEHOLDERS: Record<ResourceType, string> = {
  Human: 'e.g. skills, experience, availability, other information...',
  Equipment: 'e.g. specifications, location, usage notes...',
  Material: 'e.g. specifications, storage requirements, handling notes...',
  Subcontractor: 'e.g. scope of work, preferred roles, terms etc.',
};

interface AdditionalDataProps {
  data: ResourceDataType;
  setData: <K extends keyof ResourceDataType>(key: K, value: ResourceDataType[K]) => void;
  disabled?: boolean;
}

const AdditionalData: React.FC<AdditionalDataProps> = ({ data, setData, disabled = false }) => {
  const plainNotes = (data.notes || '').replace(/<[^>]*>?/gm, '').trim();
  const currentCount = plainNotes.length;

  const renderAccordions = () => {
    switch (data.type) {
      case 'Human':
        return (
          <>
            <CollapsibleRow
              icon={<SchoolOutlinedIcon />}
              title="Qualifications"
              subtitle="Certifications, licences, skills, training etc."
            />
            <CollapsibleRow
              icon={<DescriptionOutlinedIcon />}
              title="Links & documents"
              subtitle="CV, certificates, ID documents etc."
            />
          </>
        );
      case 'Equipment':
        return (
          <>
            <CollapsibleRow
              icon={<SettingsOutlinedIcon />}
              title="Service record"
              subtitle="Maintenance, inspections, warranties etc."
            />
            <CollapsibleRow
              icon={<DescriptionOutlinedIcon />}
              title="Links & documents"
              subtitle="Manuals, certificates, photos etc."
            />
          </>
        );
      case 'Material':
        return (
          <>
            <CollapsibleRow
              icon={<DescriptionOutlinedIcon />}
              title="Specifications & MSDS"
              subtitle="Data sheets, safety guidelines etc."
            />
            <CollapsibleRow
              icon={<AttachFileOutlinedIcon />}
              title="Links & documents"
              subtitle="Delivery notes, receipts, certifications etc."
            />
          </>
        );
      case 'Subcontractor':
        return (
          <>
            <CollapsibleRow
              icon={<AssignmentOutlinedIcon />}
              title="Company details"
              subtitle="Address, company number, tax information etc."
            />
            <CollapsibleRow
              icon={<GroupsOutlinedIcon />}
              title="Capabilities & trades"
              subtitle="Areas of work, accreditations, insurances etc."
            />
            <CollapsibleRow
              icon={<AttachFileOutlinedIcon />}
              title="Links & documents"
              subtitle="Contracts, insurances, RAMS, policies etc."
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ px: 3.5, pt: 2.5, pb: 3, mt: 1.5, backgroundColor: GREY2 }}>
      <SectionHeading title="2.  Additional information" hint="Add any relevant notes or general information." />

      <FieldLabel>Notes</FieldLabel>
      <Box sx={{ opacity: disabled ? 0.7 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
        <TextEditor
          setText={(val: string) => {
            const stripped = (val || '').replace(/<[^>]*>?/gm, '').trim();
            if (stripped.length <= 500) {
              setData('notes', val);
            }
          }}
          saveOn="update"
          text={data.notes}
          minRows={4}
          helperText={NOTES_PLACEHOLDERS[data.type]}
        />
      </Box>
      <Typography sx={{ fontSize: 11, color: GREY, textAlign: 'right', mt: 0.5, mb: 2 }}>{currentCount}/500</Typography>

      <Stack spacing={1.5}>{renderAccordions()}</Stack>
    </Box>
  );
};

export default AdditionalData;
